import { useEffect, useRef } from 'react';
import {
  Viewer,
  Cesium3DTileset,
  Cartesian3,
  Cartesian2,
  Math as CesiumMath,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
  Cesium3DTileStyle,
  GeoJsonDataSource,
  Color,
  PolygonHierarchy,
  ColorMaterialProperty,
} from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import { useSitesStore } from '../stores/sitesStore';
import { useViewerStore } from '../stores/viewerStore';
import { endpoints } from '../config/api';

function CesiumViewer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const tilesetRef = useRef<Cesium3DTileset | null>(null);
  const footprintsRef = useRef<GeoJsonDataSource | null>(null);
  const zoneEntitiesRef = useRef<string[]>([]);

  const { buildingModel, selectedSite } = useSitesStore();
  const { setViewer, setSelectedEntity, layerVisibility } = useViewerStore();

  // Initialize Cesium viewer
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return;

    const viewer = new Viewer(containerRef.current, {
      baseLayerPicker: true,
      geocoder: false,
      homeButton: true,
      sceneModePicker: true,
      navigationHelpButton: false,
      animation: false,
      timeline: false,
      fullscreenButton: false,
      vrButton: false,
      infoBox: true,
      selectionIndicator: true,
      shadows: false,
      shouldAnimate: true,
    });

    // Set default camera position (PA Warehouse - Blairsville, PA)
    viewer.camera.setView({
      destination: Cartesian3.fromDegrees(-79.22357, 40.45146, 1500),
      orientation: {
        heading: CesiumMath.toRadians(0),
        pitch: CesiumMath.toRadians(-60),
        roll: 0,
      },
    });

    // Enable depth testing for better 3D rendering
    viewer.scene.globe.depthTestAgainstTerrain = true;

    // Set up click handler for entity selection
    const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction((event: { position: Cartesian2 }) => {
      const pickedObject = viewer.scene.pick(event.position);
      if (defined(pickedObject)) {
        setSelectedEntity(pickedObject);
      } else {
        setSelectedEntity(null);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);

    viewerRef.current = viewer;
    setViewer(viewer);

    return () => {
      handler.destroy();
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy();
      }
      viewerRef.current = null;
    };
  }, [setViewer, setSelectedEntity]);

  // Fly to selected site
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !selectedSite?.latitude || !selectedSite?.longitude) return;

    // Fly to site location
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(
        selectedSite.longitude,
        selectedSite.latitude,
        2000 // altitude in meters
      ),
      orientation: {
        heading: CesiumMath.toRadians(0),
        pitch: CesiumMath.toRadians(-45),
        roll: 0,
      },
      duration: 2,
    });

    // Load building footprints for this site from API
    const loadFootprints = async () => {
      // Remove existing footprints and entities
      if (footprintsRef.current) {
        viewer.dataSources.remove(footprintsRef.current);
        footprintsRef.current = null;
      }
      // Clear all building entities from previous site
      viewer.entities.removeAll();

      try {
        const response = await fetch(endpoints.footprints(selectedSite.id), {
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Failed to fetch footprints');
        }
        const geojson = await response.json();

        if (geojson.features && geojson.features.length > 0) {
          // Create entities manually for 3D extrusion support
          const dataSource = new GeoJsonDataSource('footprints');

          for (const feature of geojson.features) {
            const props = feature.properties || {};
            const heightFt = props.height_ft || 20; // Default 20ft if not specified
            const heightMeters = heightFt * 0.3048;
            const buildingType = props.building_type || 'warehouse';

            // Color by building type
            let fillColor: Color;
            switch (buildingType) {
              case 'steel':
                fillColor = Color.fromCssColorString('#3498db').withAlpha(0.7); // Blue
                break;
              case 'pole_barn':
                fillColor = Color.fromCssColorString('#e67e22').withAlpha(0.7); // Orange
                break;
              case 'frame':
              case 'office':
                fillColor = Color.fromCssColorString('#9b59b6').withAlpha(0.7); // Purple
                break;
              default:
                fillColor = Color.fromCssColorString('#2ecc71').withAlpha(0.7); // Green
            }

            // Handle polygon coordinates
            if (feature.geometry.type === 'Polygon') {
              const coords = feature.geometry.coordinates[0];
              const positions = coords.map((c: number[]) =>
                Cartesian3.fromDegrees(c[0], c[1])
              );

              viewer.entities.add({
                name: props.name || props.code || 'Building',
                description: `
                  <table>
                    <tr><td><b>Code:</b></td><td>${props.code || 'N/A'}</td></tr>
                    <tr><td><b>Type:</b></td><td>${buildingType}</td></tr>
                    <tr><td><b>Height:</b></td><td>${heightFt} ft</td></tr>
                    <tr><td><b>Area:</b></td><td>${props.floor_area_sqft ? props.floor_area_sqft.toLocaleString() + ' sqft' : 'N/A'}</td></tr>
                  </table>
                `,
                polygon: {
                  hierarchy: new PolygonHierarchy(positions),
                  extrudedHeight: heightMeters,
                  height: 0,
                  material: new ColorMaterialProperty(fillColor),
                  outline: true,
                  outlineColor: Color.BLACK,
                  outlineWidth: 2,
                },
                properties: props,
              });
            }
          }

          footprintsRef.current = dataSource;
          console.log(`Loaded ${geojson.features.length} 3D buildings for ${selectedSite.code}`);
        }
      } catch (error) {
        console.log(`No footprints available for ${selectedSite.code}`);
      }
    };

    loadFootprints();
  }, [selectedSite]);

  // Load zone boundaries when enabled
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !selectedSite) return;

    // Remove existing zone entities
    zoneEntitiesRef.current.forEach((entityId) => {
      const entity = viewer.entities.getById(entityId);
      if (entity) {
        viewer.entities.remove(entity);
      }
    });
    zoneEntitiesRef.current = [];

    if (!layerVisibility.zones) return;

    const loadZones = async () => {
      try {
        const response = await fetch(
          endpoints.zoneBoundaries(selectedSite.id),
          { credentials: 'include' }
        );
        if (!response.ok) return;

        const geojson = await response.json();

        if (geojson.features && geojson.features.length > 0) {
          for (const feature of geojson.features) {
            const props = feature.properties || {};

            // Color based on utilization
            let zoneColor: Color;
            if (props.color) {
              zoneColor = Color.fromCssColorString(props.color).withAlpha(0.4);
            } else if (props.utilization_pct > 80) {
              zoneColor = Color.fromCssColorString('#e74c3c').withAlpha(0.4); // Red
            } else if (props.utilization_pct > 60) {
              zoneColor = Color.fromCssColorString('#f39c12').withAlpha(0.4); // Orange
            } else {
              zoneColor = Color.fromCssColorString('#27ae60').withAlpha(0.4); // Green
            }

            if (feature.geometry.type === 'Polygon') {
              const coords = feature.geometry.coordinates[0];
              const positions = coords.map((c: number[]) =>
                Cartesian3.fromDegrees(c[0], c[1])
              );

              const entityId = `zone-${props.zone_code}`;
              viewer.entities.add({
                id: entityId,
                name: props.zone_name || props.zone_code,
                description: `
                  <table style="width:100%">
                    <tr><td><b>Zone:</b></td><td>${props.zone_code}</td></tr>
                    <tr><td><b>Type:</b></td><td>${props.zone_type}</td></tr>
                    <tr><td><b>Building:</b></td><td>${props.building || 'N/A'}</td></tr>
                    <tr><td><b>Locations:</b></td><td>${props.total_locations?.toLocaleString() || 0}</td></tr>
                    <tr><td><b>Occupied:</b></td><td>${props.occupied_locations?.toLocaleString() || 0}</td></tr>
                    <tr><td><b>Utilization:</b></td><td><b style="color:${props.utilization_pct > 80 ? '#e74c3c' : props.utilization_pct > 60 ? '#f39c12' : '#27ae60'}">${props.utilization_pct || 0}%</b></td></tr>
                  </table>
                `,
                polygon: {
                  hierarchy: new PolygonHierarchy(positions),
                  height: 0.5, // Slightly above ground
                  material: new ColorMaterialProperty(zoneColor),
                  outline: true,
                  outlineColor: Color.fromCssColorString(props.color || '#ffffff'),
                  outlineWidth: 3,
                },
                properties: props,
              });
              zoneEntitiesRef.current.push(entityId);
            }
          }
          console.log(`Loaded ${geojson.features.length} zones for ${selectedSite.code}`);
        }
      } catch (error) {
        console.log(`No zone boundaries available for ${selectedSite.code}`);
      }
    };

    loadZones();
  }, [selectedSite, layerVisibility.zones]);

  // Load 3D Tiles when building model changes
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !buildingModel?.tiles_url) return;

    // Remove existing tileset
    if (tilesetRef.current) {
      viewer.scene.primitives.remove(tilesetRef.current);
      tilesetRef.current = null;
    }

    // Load new tileset
    const loadTileset = async () => {
      try {
        const tileset = await Cesium3DTileset.fromUrl(buildingModel.tiles_url);

        // Style the tileset
        tileset.style = new Cesium3DTileStyle({
          color: {
            conditions: [
              ["${element_type} === 'wall'", "color('white')"],
              ["${element_type} === 'door'", "color('brown')"],
              ["${element_type} === 'window'", "color('lightblue', 0.5)"],
              ["${element_type} === 'column'", "color('gray')"],
              ['true', "color('white')"],
            ],
          },
        });

        viewer.scene.primitives.add(tileset);
        tilesetRef.current = tileset;

        // Zoom to tileset
        await viewer.zoomTo(tileset);
      } catch (error) {
        console.error('Failed to load 3D Tiles:', error);
      }
    };

    loadTileset();
  }, [buildingModel]);

  return (
    <div ref={containerRef} className="cesium-container" />
  );
}

export default CesiumViewer;
